import React, { useState, useEffect } from 'react';
import { useGameAudio } from '../GameAudioProvider';
import { LoadingSpinner } from '../LoadingSpinner';
import { BUTTON_TEXT, UI_TEXT } from '../constants';
import { calculateLevelResults } from '../utils';

interface Question {
  questionId: number;
  questionType: 'single_select' | 'multi_select' | 'coding';
  questionText: string;
  codeSnippet?: string;
  options?: string[];
  correctAnswer: string | string[];
  codingInstructions?: string;
  expectedSolutionPattern?: string;
  evaluationCriteria?: string[];
  visualHint: string;
}

interface Level {
  levelId: number;
  levelName: string;
  difficulty: string;
  questions: Question[];
}

interface GameData {
  setId: number;
  setName: string;
  setDescription: string;
  theme: string;
  levels: Level[];
}

interface AssessmentGameProps {
  currentLevel: number;
  onLevelComplete: (score: number, totalPossible: number, starsEarned: number) => void;
}

const DEFAULT_REMOTE_DATA_URLS = [
  'https://kinzy.club/games/game/11-07-25/gjb_2130898747750615053/data_layer/current/data_files/game_data_set_1.json',
  'https://kinzy.club/games/game/11-07-25/gjb_2130898747750615053/data_layer/current/data_files/game_data_set_2.json'
];

const DEFAULT_LOCAL_DATA_URLS = [
  '/data/game_data_set_1.json',
  '/data/game_data_set_2.json'
];

const fetchDataWithRetry = async (url: string, retries = 3) => {
  for (let i = 0; i < retries; i++) {
    try {
      const cacheBustUrl = url + (url.includes('?') ? '&' : '?') + 't=' + Date.now();
      const response = await fetch(cacheBustUrl, {
        method: 'GET',
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (response.status === 304) {
        console.warn('Got 304 Not Modified, retrying...');
        await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
        continue;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (fetchError) {
      console.error(`Fetch attempt ${i + 1} failed:`, fetchError);
      if (i === retries - 1) throw fetchError;
      await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, i)));
    }
  }
};

const AssessmentGame: React.FC<AssessmentGameProps> = ({ currentLevel, onLevelComplete }) => {
  const [gameData, setGameData] = useState<GameData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, string | string[]>>({});
  const [userAnswers, setUserAnswers] = useState<Record<number, { answer: string | string[], isCorrect: boolean }>>({});
  const [codeInput, setCodeInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasCompletedLevel, setHasCompletedLevel] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(() => {
    // Question 13 in level 1 is a coding problem, needs 60 seconds
    return 20;
  });
  const { stopAudio } = useGameAudio();

  useEffect(() => {
    const loadGameData = async () => {
      try {
        setIsLoading(true);
        const envDataUrls = (import.meta.env.VITE_GAME_DATA_URLS as string | undefined)
          ?.split(',')
          .map(url => url.trim())
          .filter(Boolean);
        const dataUrls = envDataUrls?.length ? envDataUrls : DEFAULT_REMOTE_DATA_URLS;
        const fallbackDataUrls = (import.meta.env.VITE_GAME_DATA_FALLBACK_URLS as string | undefined)
          ?.split(',')
          .map(url => url.trim())
          .filter(Boolean) ?? DEFAULT_LOCAL_DATA_URLS;

        const dataPromises = dataUrls.map((url, index) =>
          fetchDataWithRetry(url).catch(error => {
            console.warn(`Falling back to local data for set ${index + 1}:`, error);
            const fallbackUrl = fallbackDataUrls[index];
            if (!fallbackUrl) {
              throw error;
            }
            return fetchDataWithRetry(fallbackUrl, 1);
          })
        );
        const loadedData = await Promise.all(dataPromises);
        setGameData(loadedData);
      } catch (error) {
        console.error('Failed to load game data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadGameData();
  }, []);

  useEffect(() => {
    // Reset state when level changes
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setHasCompletedLevel(false);
  }, [currentLevel]);

  useEffect(() => {
    // Question 13 in level 1 is a coding problem, needs 60 seconds
    const isQuestion13Level1 = currentLevel === 1 && currentQuestionIndex === 12;
    setIsSubmitting(false);
    if (isQuestion13Level1) {
      setTimeRemaining(60);
    } else {
      setTimeRemaining(20);
    }
  }, [currentQuestionIndex]);

  useEffect(() => {
    if (isSubmitting) return;

    if (timeRemaining <= 0 && !isSubmitting) {
      handleSubmit();
      return;
    }

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (isSubmitting) return prev;

        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining, currentQuestionIndex, isSubmitting]);

  if (isLoading) {
    return <LoadingSpinner message="Loading assessment..." />;
  }

  if (gameData.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 shadow-2xl text-center max-w-md">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-800 mb-4">Failed to Load Assessment</h2>
          <p className="text-gray-600">Please refresh the page to try again.</p>
        </div>
      </div>
    );
  }

  const currentSet = gameData[currentLevel - 1];
  if (!currentSet) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 shadow-2xl text-center max-w-md">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-800 mb-4">Invalid Level Data</h2>
          <p className="text-gray-600">This level does not contain valid questions.</p>
        </div>
      </div>
    );
  }

  // Flatten all questions from all levels in the current set
  const questions = currentSet.levels?.reduce((allQuestions: Question[], level: Level) => {
    return [...allQuestions, ...(level.questions || [])];
  }, []) || [];

  if (questions.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 shadow-2xl text-center max-w-md">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-800 mb-4">Invalid Level Data</h2>
          <p className="text-gray-600">This level does not contain valid questions.</p>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const totalQuestions = questions.length;

  const handleAnswerSelect = (answer: string) => {
    stopAudio();
    if (currentQuestion.questionType === 'multi_select') {
      const current = (selectedAnswers[currentQuestion.questionId] as string[]) || [];
      const updated = current.includes(answer)
        ? current.filter(a => a !== answer)
        : [...current, answer];
      setSelectedAnswers({ ...selectedAnswers, [currentQuestion.questionId]: updated });
    } else {
      setSelectedAnswers({ ...selectedAnswers, [currentQuestion.questionId]: answer });
    }
  };

  const checkAnswer = (userAnswer: string | string[], correctAnswer: string | string[]): boolean => {
    if (Array.isArray(correctAnswer)) {
      if (!Array.isArray(userAnswer)) return false;
      return correctAnswer.length === userAnswer.length && correctAnswer.every(a => userAnswer.includes(a));
    }
    return userAnswer === correctAnswer;
  };

  const handleSubmit = () => {
    stopAudio();
    // Allow submission even if isSubmitting is true when called by timer
    
    if (hasCompletedLevel) {
      return;
    }

    setIsSubmitting(true);

    const answer = currentQuestion.questionType === 'coding' ? codeInput : (selectedAnswers[currentQuestion.questionId] || '');
    
    // For coding questions, allow empty submissions when timer runs out
    const isCorrect = checkAnswer(answer, currentQuestion.correctAnswer);

    setUserAnswers({
      ...userAnswers,
      [currentQuestion.questionId]: { answer, isCorrect }
    });

    if (currentQuestionIndex < totalQuestions - 1) {
      setTimeout(() => {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        setCodeInput('');
      }, 0);
    } else {
      const correctCount = Object.values({ ...userAnswers, [currentQuestion.questionId]: { answer, isCorrect } })
        .filter(a => a.isCorrect).length;
      const results = calculateLevelResults(correctCount, totalQuestions);
      setTimeout(() => {
        setHasCompletedLevel(true);
        onLevelComplete(results.score, results.totalPossible, results.starsEarned);
      }, 0);
    }
  };

  const isAnswerSelected = currentQuestion.questionType === 'coding'
    ? codeInput.trim().length > 0
    : (timeRemaining <= 0 || 
       (selectedAnswers[currentQuestion.questionId] !== undefined &&
      (Array.isArray(selectedAnswers[currentQuestion.questionId])
        ? (selectedAnswers[currentQuestion.questionId] as string[]).length > 0
        : true)));

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 pt-24 pb-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-8">
          <div className="mb-6">
            <div className="flex items-center justify-center mb-4">
              <div className={`text-4xl font-bold ${
                timeRemaining <= 5 ? 'text-red-500 animate-pulse' : 'text-blue-600'
              }`}>
                {Math.max(0, timeRemaining)}s
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
              <div
                className={`h-3 rounded-full transition-all duration-1000 ${
                  timeRemaining <= 5 ? 'bg-red-500' : 'bg-gradient-to-r from-green-500 to-blue-500'
                }`}
                style={{ width: `${(Math.max(0, timeRemaining) / (currentLevel === 1 && currentQuestionIndex === 12 ? 60 : 20)) * 100}%` }}
              />
            </div>
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-semibold text-blue-600">
                {UI_TEXT.QUESTION_OF} {currentQuestionIndex + 1} of {totalQuestions}
              </span>
              <span className="text-sm font-semibold text-purple-600">
                Level {currentLevel}: {currentSet.setName}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${((currentQuestionIndex + 1) / totalQuestions) * 100}%` }}
              />
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-4">
              {currentQuestion.questionText}
              {currentQuestion.questionType === 'multi_select' && (
                <span className="text-sm text-gray-500 ml-2">{UI_TEXT.SELECT_ALL}</span>
              )}
            </h2>

            {currentQuestion.codeSnippet && (
              <pre className="bg-gray-900 text-green-400 p-4 rounded-lg mb-4 overflow-x-auto text-sm">
                <code>{currentQuestion.codeSnippet}</code>
              </pre>
            )}
          </div>

          {currentQuestion.questionType === 'coding' ? (
            <div className="mb-6">
              {currentQuestion.codingInstructions && (
                <p className="text-gray-600 mb-4">{currentQuestion.codingInstructions}</p>
              )}
              <textarea
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value)}
                className="w-full h-64 p-4 font-mono text-sm bg-gray-900 text-green-400 rounded-lg border-2 border-gray-700 focus:border-blue-500 focus:outline-none"
                placeholder="Write your code here..."
                spellCheck={false}
              />
            </div>
          ) : (
            <div className="space-y-3 mb-6">
              {currentQuestion.options?.map((option, index) => {
                const isSelected = currentQuestion.questionType === 'multi_select'
                  ? ((selectedAnswers[currentQuestion.questionId] as string[]) || []).includes(option)
                  : selectedAnswers[currentQuestion.questionId] === option;

                return (
                  <button
                    key={index}
                    onClick={() => handleAnswerSelect(option)}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                      isSelected
                        ? 'bg-blue-50 border-blue-500 shadow-md'
                        : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-center">
                      <div className={`w-5 h-5 rounded ${
                        currentQuestion.questionType === 'multi_select' ? 'rounded-sm' : 'rounded-full'
                      } border-2 mr-3 flex items-center justify-center ${
                        isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                      }`}>
                        {isSelected && (
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <span className="text-gray-800">{option}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={!isAnswerSelected}
            className={`w-full py-4 px-8 text-lg font-bold rounded-xl transition-all duration-300 ${
              isAnswerSelected
                ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:shadow-xl hover:scale-105 active:scale-95'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {currentQuestionIndex < totalQuestions - 1 ? BUTTON_TEXT.NEXT_QUESTION : BUTTON_TEXT.SUBMIT_ANSWER}
          </button>
        </div>
      </div>
    </div>
  );
};

export { AssessmentGame };
export type { AssessmentGameProps };
export default AssessmentGame;